import {
    useState,
    createContext,
    createElement,
    ReactNode,
} from "react";
import {
    StyleTypes,
    BackgroundTypes,
    PreSaveErrorTypes as ErrorTypes,
    TitleAlignments,
} from "modules/Types";
import moment from "moment";
import isEmpty from "lodash/isEmpty";
import { CustomLink } from "../PreSave/Components/AddEditCustomLinkModal/AddEditCustomLinkModal";
import { SocialConnectType } from "modules/Const";
import SpotifyIcon from "assets/images/streaming/square-icons/spotify-sqr.svg";
import DeezerIcon from "assets/images/streaming/square-icons/deezer-sqr.svg";
import AppleMusicIcon from "assets/images/streaming/square-icons/apple-sqr.svg";
import AudiomackIcon from "assets/images/streaming/square-icons/audiomack-sqr.svg";
import SoundcloudIcon from "assets/images/streaming/square-icons/soundcloud-sqr.svg";

// Platform configuration - centralized source of truth for all platforms
// This makes adding new platforms as simple as adding a new entry here
export interface PlatformConfig {
    id: string;
    name: string;
    label: string;
    icon: string;
    defaultEnabled: boolean;
    alwaysEnabled: boolean; // Cannot be disabled (like Spotify)
    autofollowAllowed: boolean;
    socialConnectType?: SocialConnectType;
}

export const PLATFORM_CONFIGS: Record<string, PlatformConfig> = {
    spotify: {
        id: 'spotify',
        name: 'Spotify',
        label: 'Pre-save on Spotify',
        icon: SpotifyIcon,
        defaultEnabled: true,
        alwaysEnabled: true, // Spotify can't be disabled
        autofollowAllowed: true,
    },
    deezer: {
        id: 'deezer',
        name: 'Deezer',
        label: 'Pre-save on Deezer',
        icon: DeezerIcon,
        defaultEnabled: true,
        alwaysEnabled: false,
        autofollowAllowed: true,
        socialConnectType: SocialConnectType.DEEZER,
    },
    apple_music: {
        id: 'apple_music',
        name: 'Apple Music',
        label: 'Pre-add on Apple Music',
        icon: AppleMusicIcon,
        defaultEnabled: true,
        alwaysEnabled: false,
        autofollowAllowed: false,
    },
    audiomack: {
        id: 'audiomack',
        name: 'Audiomack',
        label: 'Pre-save on Audiomack',
        icon: AudiomackIcon,
        defaultEnabled: true,
        alwaysEnabled: false,
        autofollowAllowed: true,
        socialConnectType: SocialConnectType.AUDIOMACK,
    },
    soundcloud: {
        id: 'soundcloud',
        name: 'SoundCloud',
        label: 'Pre-save on SoundCloud',
        icon: SoundcloudIcon,
        defaultEnabled: true,
        alwaysEnabled: false,
        autofollowAllowed: true,
        socialConnectType: SocialConnectType.SOUNDCLOUD,
    },
};

export interface ExternalLink {
    id: string;
    type: 'platform' | 'custom';
    name: string; // Platform ID for platforms, title for custom links
    enabled: boolean;
    // For custom links
    url?: string;
    icon?: string; // Platform ID (e.g., 'spotify', 'apple_music') for custom links that want to use platform icons
}

export interface Details {
    releaseUrl: string;
    releaseUrlValid: boolean;
    releaseDate: string | null;
    // Legacy fields - keep for backward compatibility during transition
    musicPlatforms: { name: string; show: boolean }[];
    customLinks: CustomLink[];
    // New unified structure
    external_links: ExternalLink[];
    timezone: {
        value: string;
        abbrev: string;
    };
    boosts: {
        collectEmails: boolean;
        boostFollows: boolean;
        autoPresaves: boolean;
    };
    localRelease: boolean;
    hideReleaseDate: boolean;
}

export interface Customize {
    artwork: string | null;
    releaseTitle: string;
    styleType: StyleTypes;
    backgroundType: BackgroundTypes;
    primaryColor: string;
    buttonColor: string;
    sendFansToCustomWebPage: boolean;
    customWebPageUrl: string;
    backgroundColor: string;
    subtitle: string;
    showSecondaryLine: boolean;
    titleAlignment: TitleAlignments;
}

interface Context {
    details: Details;
    customize: Customize;
    showPostCreation: boolean;
    loading: boolean;
    postCreationErrorType: ErrorTypes | null;
    setPostCreationErrorType: (errorType: ErrorTypes | null) => void;
    saveDetails: (details: Details) => void;
    saveCustomize: (customize: Customize) => void;
    setShowPostCreation: (value: boolean) => void;
    setLoading: (value: boolean) => void;
    dataComplete: () => boolean
}

// Helper function to convert legacy format to unified external_links
const createDefaultExternalLinks = (): ExternalLink[] => {
    return Object.values(PLATFORM_CONFIGS)
        .filter(config => config.defaultEnabled)
        .map(config => ({
            id: config.id,
            type: 'platform' as const,
            name: config.id,
            enabled: config.defaultEnabled,
        }));
};

// Migration utility to convert legacy musicPlatforms + customLinks to external_links
export const migrateToExternalLinks = (
    musicPlatforms: { name: string; show: boolean }[], 
    customLinks: CustomLink[]
): ExternalLink[] => {
    const platformLinks: ExternalLink[] = musicPlatforms.map(platform => ({
        id: platform.name,
        type: 'platform' as const,
        name: platform.name,
        enabled: platform.show,
    }));

    const customLinkItems: ExternalLink[] = customLinks.map(link => ({
        id: link.id,
        type: 'custom' as const,
        name: link.title,
        enabled: true,
        url: link.url,
        icon: link.icon,
    }));

    return [...platformLinks, ...customLinkItems];
};

// Utility to extract legacy formats from external_links (for backward compatibility)
export const extractLegacyFormats = (external_links: ExternalLink[]) => {
    const musicPlatforms = external_links
        .filter(link => link.type === 'platform')
        .map(link => ({ name: link.name, show: link.enabled }));

    const customLinks = external_links
        .filter(link => link.type === 'custom')
        .map(link => ({
            id: link.id,
            type: 'custom' as const,
            title: link.name,
            url: link.url || '',
            icon: link.icon,
        }));

    return { musicPlatforms, customLinks };
};

const DEFAULT_DETAILS: Details = {
    releaseUrl: '',
    releaseUrlValid: false,
    releaseDate: null,
    // Legacy fields - keep for backward compatibility
    musicPlatforms: Object.values(PLATFORM_CONFIGS)
        .filter(config => config.defaultEnabled)
        .map(config => ({ name: config.id, show: config.defaultEnabled })),
    customLinks: [],
    // New unified structure
    external_links: createDefaultExternalLinks(),
    boosts: {
        collectEmails: true,
        boostFollows: true,
        autoPresaves: true,
    },
    timezone: { value: '', abbrev: '' },
    localRelease: false,
    hideReleaseDate: false,
};

const DEFAULT_CUSTOMIZE: Customize = {
    artwork: null,
    releaseTitle: '',
    styleType: StyleTypes.BOXES,
    backgroundType: BackgroundTypes.ARTWORK,
    primaryColor: '',
    buttonColor: '',
    sendFansToCustomWebPage: false,
    customWebPageUrl: '',
    backgroundColor: '',
    subtitle: '',
    showSecondaryLine: false,
    titleAlignment: TitleAlignments.CENTER,
};

export const PreSaveDataContext = createContext<Context>({
    details: DEFAULT_DETAILS,
    customize: DEFAULT_CUSTOMIZE,
    showPostCreation: false,
    loading: true,
    postCreationErrorType: null,
    saveDetails: () => {},
    saveCustomize: () => {},
    setShowPostCreation: () => {},
    setLoading: () => {},
    setPostCreationErrorType: () => {},
    dataComplete: () => false,
});

type PreSaveProviderProps = {
    children: ReactNode[] | ReactNode;
};

const PreSaveProvider = ({
    children
}: PreSaveProviderProps) => {
    const [details, saveDetails] = useState<Details>(DEFAULT_DETAILS);
    const [customize, saveCustomize] = useState<Customize>(DEFAULT_CUSTOMIZE);
    const [showPostCreation, setShowPostCreation] = useState<boolean>(false);
    const [loading, setLoading] = useState(true);
    const [postCreationErrorType, setPostCreationErrorType] = useState<ErrorTypes | null>(null);

    const onSaveDetails = (incomingDetails: Details) => {
        // Ensure external_links is up to date - if it's empty or missing, migrate from legacy format
        let updatedDetails = { ...incomingDetails };
        
        if (!updatedDetails.external_links || updatedDetails.external_links.length === 0) {
            updatedDetails.external_links = migrateToExternalLinks(
                updatedDetails.musicPlatforms, 
                updatedDetails.customLinks
            );
        } else {
            // Sync legacy formats from external_links for backward compatibility
            const { musicPlatforms, customLinks } = extractLegacyFormats(updatedDetails.external_links);
            updatedDetails.musicPlatforms = musicPlatforms;
            updatedDetails.customLinks = customLinks;
        }
        
        saveDetails(updatedDetails);
    };
    const onSaveCustomize = (customize: Customize) => saveCustomize(customize);
    const onSetShowPostCreation = (value: boolean) => setShowPostCreation(value);
    const onSetLoading = (value: boolean) => setLoading(value);
    const onSetPostCreationErrorType = (errorType: ErrorTypes | null) => setPostCreationErrorType(errorType);

    const returnDataComplete = () => {
        // confirm all details avail
        return Boolean(
            (isEmpty(details.releaseUrl) || details.releaseUrlValid) &&
            customize.releaseTitle &&
            details.releaseDate &&
            moment(details.releaseDate).isValid() &&
            details.musicPlatforms.length > 0 &&
            details.timezone.value &&
            customize.styleType &&
            customize.backgroundType
        )
    }

    const presaveContext = {
        details,
        customize,
        showPostCreation,
        loading,
        postCreationErrorType,
        saveDetails: onSaveDetails,
        saveCustomize: onSaveCustomize,
        setShowPostCreation: onSetShowPostCreation,
        setLoading: onSetLoading,
        setPostCreationErrorType: onSetPostCreationErrorType,
        dataComplete: returnDataComplete
    };

    return createElement(
        PreSaveDataContext.Provider,
        { value: presaveContext },
        children
    );
};

export default PreSaveProvider;
